import React, { useEffect, useMemo, useRef } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { usePageMeta } from '../../lib/seo/use-page-meta'
import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { blogServices } from '../../services/blog-service'
import { getFormattedDate } from '../../lib/utils/helpers'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'
import { CATEGORIES } from '../blog/blog'
import './blog-details.scss'
import { LinkedInIcon } from '../../lib/components/Icon'
import { BlogReadingTime } from '../../lib/components/BlogReadingTime/blog-reading-time'

interface BlogPost {
  id: number
  title: string
  sub_title: string
  slug: string
  content: string
  category: string
  banner_image: string
  created_at: string
  linkedin_url?: string
  reading_time_minutes?: number | null
  updated_at?: string
}

interface RelatedPost {
  id: number
  title: string
  sub_title: string
  slug: string
  banner_image: string
  category: string
  created_at: string
  linkedin_url?: string
}

const DATE_FORMAT = 'MMMM DD, YYYY'

export const BlogDetailsPage = () => {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { authToken } = useSelector((state: RootState) => state.investor)
  const loggedSlugRef = useRef<string | null>(null)

  const { data, isFetching, isError } = useQuery({
    queryKey: ['blogPost', slug],
    queryFn: async () => {
      const response = await blogServices.getBlogPostBySlug(slug ?? '', {
        Authorization: authToken ?? '',
      })
      return response?.data
    },
    enabled: !!slug,
  })

  const { data: relatedData } = useQuery({
    queryKey: ['relatedBlogPosts', slug],
    queryFn: async () => {
      return blogServices.getBlogPosts(
        { limit: '4', offset: '0', category: '' },
        { Authorization: authToken ?? '' },
      )
    },
  })

  useEffect(() => {
    if (!slug || loggedSlugRef.current === slug) return
    loggedSlugRef.current = slug
    logCrmEvent(EngagementEvents.VISITED_BLOG, { Authorization: authToken ?? '' })
  }, [slug, authToken])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [slug])

  const post: BlogPost | undefined = data?.blogPost ?? data
  const articleJsonLd = useMemo(() => {
    if (!post?.title || !post.slug) return ''
    const url = `${window.location.origin}/insights/posts/${post.slug}`
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.sub_title,
      image: post.banner_image ? [post.banner_image] : undefined,
      datePublished: post.created_at,
      dateModified: post.updated_at || post.created_at,
      mainEntityOfPage: url,
      author: { '@type': 'Organization', name: 'Credibila' },
      publisher: {
        '@type': 'Organization',
        name: 'Credibila',
        logo: {
          '@type': 'ImageObject',
          url: `${window.location.origin}/credibilaLogo.png`,
        },
      },
    })
  }, [post])

  usePageMeta({
    enabled: Boolean(post?.title),
    title: post?.title ? `${post.title} | Credibila Insights` : 'Credibila',
    description: post?.sub_title || 'Credibila insights',
    canonicalPath: post?.slug ? `/insights/posts/${post.slug}` : '/insights',
    image: post?.banner_image,
    ogType: 'article',
    jsonLd: articleJsonLd,
  })

  const relatedPosts: RelatedPost[] = (relatedData?.blogPosts ?? [])
    .filter((p: RelatedPost) => p.slug !== slug)
    .slice(0, 3)

  const handleRelatedClick = (postSlug: string) => {
    navigate(`/insights/posts/${postSlug}`)
  }

  if (isFetching) {
    return (
      <div className='bd-page'>
        <div className='bd-loading'>
          <div className='bd-loading__spinner' />
        </div>
      </div>
    )
  }

  if (isError || !post) {
    return (
      <div className='bd-page'>
        <div className='bd-error'>
          <h2 className='bd-error__title'>Article not found</h2>
          <p className='bd-error__text'>
            The article you&apos;re looking for doesn&apos;t exist or has been removed.
          </p>
          <Link className='bd-error__btn' to='/insights'>
            Back to Blog
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className='bd-page'>
      {/* Hero header */}
      <header className='bd-hero'>
        <div className='bd-hero__inner'>
          <span className='bd-hero__category'>
            {CATEGORIES.find((c) => c.value === post.category)?.text}
          </span>
          <h1 className='bd-hero__title'>{post.title}</h1>
          <BlogReadingTime
            minutes={post.reading_time_minutes}
            rootClassName='bd-hero__read-time'
            iconClassName='bd-hero__read-time-icon'
          />
          <p className='bd-hero__subtitle'>{post.sub_title}</p>
        </div>
      </header>

      {/* Banner image */}
      {post.banner_image && (
        <div className='bd-banner'>
          <img className='bd-banner__img' src={post.banner_image} alt={post.title} />

          {/* Article date */}
          <time className='bd-hero__date'>
            {getFormattedDate(new Date(post.created_at), DATE_FORMAT)}
          </time>
        </div>
      )}

      {/* Article content */}
      <article className='bd-article'>
        <div className='bd-article__content' dangerouslySetInnerHTML={{ __html: post.content }} />
      </article>

      {/* Related articles */}
      {relatedPosts.length > 0 && (
        <section className='bd-related'>
          <h2 className='bd-related__heading'>Related Content</h2>
          <div className='bd-related__grid'>
            {relatedPosts.map((rp) => (
              <article
                key={rp.id}
                className='bd-related__card'
                onClick={() => handleRelatedClick(rp.slug)}
              >
                <div className='bd-related__card-img-wrap'>
                  <Link className='bd-related__card-img-link' to={`/insights/posts/${rp.slug}`}>
                    <img
                      className='bd-related__card-img'
                      src={rp.banner_image}
                      alt={rp.title}
                      loading='lazy'
                    />
                  </Link>
                </div>
                <div className='bd-related__card-body'>
                  <span className='bd-related__card-category'>
                    {CATEGORIES.find((c) => c.value === rp.category)?.text}
                  </span>
                  <h3 className='bd-related__card-title'>
                    <Link className='bd-related__card-title-link' to={`/insights/posts/${rp.slug}`}>
                      {rp.title}
                    </Link>
                  </h3>
                  <p className='bd-related__card-excerpt'>{rp.sub_title}</p>
                  <time className='bd-related__card-date'>
                    {getFormattedDate(new Date(rp.created_at), DATE_FORMAT)}
                  </time>
                  {rp.linkedin_url && (
                    <a
                      className='bd-related__card-linkedin-link'
                      href={rp.linkedin_url}
                      target='_blank'
                      rel='noopener noreferrer'
                      onClick={(event) => event.stopPropagation()}
                    >
                      <LinkedInIcon width='24' height='24' />
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* Back to blog */}
      <div className='bd-back'>
        <Link className='bd-back__btn' to='/insights'>
          <span className='bd-back__btn-icon'>&larr;</span> Back to all articles
        </Link>
      </div>

      {/* Newsletter CTA */}
      <section className='bd-cta'>
        <div className='bd-cta__inner'>
          <h3 className='bd-cta__title'>Stay up to date</h3>
          <p className='bd-cta__text'>
            Get the latest insights on private markets and alternative investments delivered
            straight to your inbox.
          </p>
          <button className='bd-cta__btn' onClick={() => navigate('/newsletter')} type='button'>
            Subscribe to newsletter
          </button>
        </div>
      </section>
    </div>
  )
}
